import { FormStore, FormValidator, IJsonSchema } from '@estee/elc-form';
import { action, observable, set } from 'mobx';
import { StringSchema, string as yupString } from 'yup';
import CompanyBillingDetails from '../CompanyBillingDetails';
import Config from '../Config';
import { IAddressSchemaProperties } from '../../../api/JsonSchemaApi';
import { FIELD_TYPE } from '../../../constants/Constants';
import { IAddressAttributes } from '../Address';

class CompanyBillingFormStore extends FormStore<IAddressAttributes> {
  @observable config: Config;

  constructor() {
    super({
      formValidator: new FormValidator(),
      initialValues: {
        [FIELD_TYPE.BILL_TO_COMPANY]: false,
        [FIELD_TYPE.COMPANY_ADDRESS]: '',
        [FIELD_TYPE.COMPANY_NAME]: '',
        [FIELD_TYPE.COMPANY_TAX_NUMBER]: '',
      },
    });
    (this.formValidator as FormValidator).registerValidation('taiwanUBN', this.validateTaiwanUBN);
  }

  public validateTaiwanUBN = (errorMessage: string): StringSchema => {
    return yupString().test('is-valid-ubn', errorMessage, this.isValidUBN);
  };

  /**
   * A UBN (Unified Business Number) in Taiwan is an 8-digit government-assigned
   * identifier required for every registered business or company. It is used for
   * taxation, VAT, and official invoicing.
   * Validation is performed with the official checksum rule, where each digit is
   * multiplied by a fixed weight and the resulting sums are checked, with a special
   * case for numbers whose 7th digit is 7.
   */
  public isValidUBN = (ubn = ''): boolean => {
    const ubnStr = ubn.trim();

    if (!/^\d{8}$/.test(ubnStr)) {
      return false;
    }

    const weights = [1, 2, 1, 2, 1, 2, 4, 1];

    let baseSum = 0;
    for (let i = 0; i < 8; i++) {
      if (i === 6) continue;

      const product = parseInt(ubnStr[i], 10) * weights[i];
      baseSum += Math.floor(product / 10) + (product % 10);
    }

    // Special handling for the 7th digit (index 6)
    const seventhDigit = parseInt(ubnStr[6], 10);
    const seventhProduct = seventhDigit * weights[6];

    // If the 7th digit is 7, 7 * 4 = 28. Digits sum: 2 + 8 = 10.
    // In old systems, 10 could be counted as either '1' (1+0) or '0'.
    // The official guidance allows testing both variations.
    if (seventhDigit === 7) {
      // Option A: 2 + 8 = 10 -> treated as 1
      const sumOptionA = baseSum + 1;
      // Option B: treated as 0
      const sumOptionB = baseSum + 0;

      return sumOptionA % 5 === 0 || sumOptionB % 5 === 0;
    } else {
      const seventhSum = Math.floor(seventhProduct / 10) + (seventhProduct % 10);
      const finalSum = baseSum + seventhSum;

      return finalSum % 5 === 0;
    }
  };

  public getCompanyBillingDetailsValidationSchema(validationSchema: IAddressSchemaProperties): IJsonSchema {
    if (!validationSchema) {
      return null;
    }

    return {
      properties: {
        ...validationSchema,
      },
      required: (Object.keys(validationSchema) as Array<keyof typeof validationSchema>).filter(
        key => validationSchema[key].required,
      ),
    };
  }

  @action
  public setCompanyBillingDetails(companyDetails: CompanyBillingDetails): CompanyBillingFormStore {
    this.setValues(companyDetails.attributes);
    return this;
  }

  public toCompanyBillingDetails(): CompanyBillingDetails {
    const companyBillingDetails = new CompanyBillingDetails();

    set(companyBillingDetails.attributes, this.values);

    return companyBillingDetails;
  }
}

export default CompanyBillingFormStore;
